import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { logActivity } from "@/lib/cms";
import { useToast } from "@/context/ToastContext";
import type { Technology } from "@/lib/types";
import {
  PageHeader,
  Button,
  Card,
  Spinner,
  EmptyState,
  Toggle,
  ConfirmDialog,
  Field,
  Input,
  Select,
  Textarea,
  Modal,
} from "@/pages/admin/components/ui";

const CATEGORIES = ["Frontend", "Backend", "Mobile", "Desktop", "Database", "Integrations", "Deployment", "Marketing"];

interface Draft {
  nombre: string;
  icono: string;
  categoria: string;
  descripcion: string;
  orden: number;
  activo: boolean;
}

export default function Tecnologias() {
  const { success, error: toastError } = useToast();
  const [items, setItems] = useState<Technology[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Technology | null>(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Draft>({ nombre: "", icono: "ri-code-line", categoria: "Frontend", descripcion: "", orden: 0, activo: true });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<Technology | null>(null);

  const load = useCallback(async () => {
    const { data } = await supabase.from("technologies").select("*").order("orden");
    setItems((data ?? []) as Technology[]);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  function openNew() {
    setEditing(null);
    setDraft({ nombre: "", icono: "ri-code-line", categoria: "Frontend", descripcion: "", orden: items.length + 1, activo: true });
    setOpen(true);
  }

  function openEdit(t: Technology) {
    setEditing(t);
    setDraft({ nombre: t.nombre, icono: t.icono || "ri-code-line", categoria: t.categoria || "Frontend", descripcion: t.descripcion || "", orden: t.orden, activo: t.activo });
    setOpen(true);
  }

  async function save() {
    if (!draft.nombre.trim()) {
      toastError("El nombre es obligatorio.");
      return;
    }
    setSaving(true);
    const payload = { nombre: draft.nombre, icono: draft.icono || null, categoria: draft.categoria || null, descripcion: draft.descripcion || null, orden: draft.orden, activo: draft.activo };
    const { error } = editing
      ? await supabase.from("technologies").update(payload).eq("id", editing.id)
      : await supabase.from("technologies").insert(payload);
    setSaving(false);
    if (error) {
      toastError("No se pudieron guardar los cambios.");
      return;
    }
    await logActivity(editing ? "modificó una tecnología" : "creó una tecnología", draft.nombre);
    success("Tecnología guardada correctamente.");
    setOpen(false);
    load();
  }

  async function toggle(t: Technology) {
    await supabase.from("technologies").update({ activo: !t.activo }).eq("id", t.id);
    load();
  }

  async function move(t: Technology, dir: -1 | 1) {
    const sorted = [...items].sort((a, b) => a.orden - b.orden);
    const idx = sorted.findIndex((x) => x.id === t.id);
    const target = idx + dir;
    if (idx < 0 || target < 0 || target >= sorted.length) return;
    await supabase.from("technologies").update({ orden: sorted[target].orden }).eq("id", t.id);
    await supabase.from("technologies").update({ orden: sorted[idx].orden }).eq("id", sorted[target].id);
    load();
  }

  async function confirmDelete() {
    if (!deleting) return;
    const { error } = await supabase.from("technologies").delete().eq("id", deleting.id);
    if (error) {
      toastError("No se pudo eliminar la tecnología.");
      return;
    }
    await logActivity("eliminó una tecnología", deleting.nombre);
    success("Tecnología eliminada.");
    setDeleting(null);
    load();
  }

  const sorted = [...items].sort((a, b) => a.orden - b.orden);

  return (
    <div>
      <PageHeader
        title="Tecnologías"
        subtitle={`${items.length} tecnologías en total`}
        actions={<Button onClick={openNew}><i className="ri-add-line" />Nueva tecnología</Button>}
      />

      {loading ? (
        <Spinner label="Cargando tecnologías…" />
      ) : items.length === 0 ? (
        <Card><EmptyState icon="ri-code-s-slash-line" title="Aún no hay tecnologías" action={<Button onClick={openNew}><i className="ri-add-line" />Nueva tecnología</Button>} /></Card>
      ) : (
        <div className="space-y-3">
          {sorted.map((t, i) => (
            <Card key={t.id} className="p-4 flex items-center gap-4">
              <div className="w-11 h-11 rounded-lg bg-accent-50 text-accent-600 flex items-center justify-center shrink-0">
                <i className={`${t.icono || "ri-code-line"} text-xl`} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-medium text-foreground-900">{t.nombre}</p>
                <p className="text-xs text-foreground-500">{t.categoria || "Sin categoría"}</p>
              </div>
              <Toggle checked={t.activo} onChange={() => toggle(t)} />
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => move(t, -1)} disabled={i === 0} className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 disabled:opacity-30"><i className="ri-arrow-up-line" /></button>
                <button onClick={() => move(t, 1)} disabled={i === sorted.length - 1} className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 disabled:opacity-30"><i className="ri-arrow-down-line" /></button>
                <button onClick={() => openEdit(t)} className="w-8 h-8 flex items-center justify-center rounded-md text-primary-600 hover:bg-primary-50"><i className="ri-edit-line" /></button>
                <button onClick={() => setDeleting(t)} className="w-8 h-8 flex items-center justify-center rounded-md text-red-500 hover:bg-red-50"><i className="ri-delete-bin-line" /></button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={editing ? "Editar tecnología" : "Nueva tecnología"}
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving}>{saving ? "Guardando…" : "Guardar"}</Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label="Nombre" required>
            <Input value={draft.nombre} onChange={(e) => setDraft((d) => ({ ...d, nombre: e.target.value }))} />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Categoría">
              <Select value={draft.categoria} onChange={(e) => setDraft((d) => ({ ...d, categoria: e.target.value }))}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </Select>
            </Field>
            <Field label="Icono (Remix)" hint="Ej. ri-reactjs-line">
              <Input value={draft.icono} onChange={(e) => setDraft((d) => ({ ...d, icono: e.target.value }))} />
            </Field>
          </div>
          <Field label="Descripción">
            <Textarea rows={2} value={draft.descripcion} onChange={(e) => setDraft((d) => ({ ...d, descripcion: e.target.value }))} />
          </Field>
          <div className="flex items-center gap-6">
            <Field label="Orden">
              <Input type="number" value={draft.orden} onChange={(e) => setDraft((d) => ({ ...d, orden: Number(e.target.value) }))} className="w-24" />
            </Field>
            <div className="pt-5">
              <Toggle checked={draft.activo} onChange={(v) => setDraft((d) => ({ ...d, activo: v }))} label="Activo" />
            </div>
          </div>
        </div>
      </Modal>

      <ConfirmDialog open={Boolean(deleting)} title="Eliminar tecnología" message={`¿Seguro que quieres eliminar "${deleting?.nombre}"?`} onConfirm={confirmDelete} onCancel={() => setDeleting(null)} />
    </div>
  );
}